'use client'
import { ClickableDecision, VideoSpotlightDecision } from '@/app/api/placements/types'
import ClickableAd from '@/app/components/ad-grid/clickable-ad'
import SpotlightContainer from '@/app/components/ad-grid/container'
import { clickableAdWidth } from '@/app/components/ad-grid/spotlight-util'
import VideoSpotlight from '@/app/components/ad-grid/video-spotlight'
import FreestarPlacement from '@/app/components/freestar'
import type { FreestarOpportunity } from '@/app/components/freestar/types'
import Loader from '@/app/components/loader'
import { SearchTypeCopy, SearchTypeToFetchType } from '@/app/util/search-util'
import { useGiphyFetch } from '@/app/util/use-gf'
import { useIsMobile } from '@/app/util/use-media-query'
import Grid from '@/components/grid/grid'
import { useAdOffsets } from '@/components/grid/use-ad-offsets'
import VideoGrid from '@/components/grid/video-grid'
import { SectionHeader } from '@/components/sections'
import Toggle from '@/components/toggle'
import UAParserContext from '@/context/ua-parser'
import { relativeGifClick } from '@/util/url'
import useClientRender from '@/util/use-client-render'
import { GifID, IGif } from '@giphy/js-types'
import { setGADataLayer } from 'analytics'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { SearchType } from '../../search/types'
import { GifsContext } from '../gifs-context'

type Props = {
    gif: IGif
    videoData?: VideoSpotlightDecision
    clickableAd?: ClickableDecision
    initialGifs?: IGif[]
    freestarOpportunity?: FreestarOpportunity
}
const RelatedGrid = ({ gif, videoData, clickableAd, initialGifs, freestarOpportunity }: Props) => {
    const id = gif.id as string
    const { deviceType } = useContext(UAParserContext)

    let width
    let columns
    if (deviceType === 'desktop') {
        width = 1040
        columns = 4
    }

    const isMobile = useIsMobile()
    const clientRender = useClientRender()
    const { width: windowWidth } = useWindowSize()

    const gf = useGiphyFetch()
    // save these to report the index of the related gif clicked
    const ids = useRef<GifID[]>([])
    const contentTypes = ['gifs', 'stickers', 'clips'] as const

    const [isTransitioning, setTransitioning] = useState(false)
    const [type, setType] = useState<SearchType>(gif.is_sticker ? 'stickers' : 'gifs')

    const doFetch = useCallback(
        async (offset: number) => {
            const result = await gf.related(id, { offset, rating: 'pg-13', type: SearchTypeToFetchType[type] })
            ids.current = result.data.map((gif) => gif.id)
            return result
        },
        [gf, id, type]
    )
    const { relatedGifs: _relatedGifs } = useContext(GifsContext)

    const relatedGifs = useMemo(() => {
        // filter out the current id
        return [...(initialGifs || []), ..._relatedGifs].filter((gif) => gif.id !== id)
    }, [_relatedGifs, id, initialGifs])

    const { columnOffsets } = useAdOffsets({
        clickableAdGif: clickableAd?.gif || null,
        spotlightGif: videoData?.gif || null,
        includeSpotlight: !!videoData,
        isMobile,
        windowWidth,
        type,
        clientRender,
    })

    return relatedGifs.length > 0 ? (
        <section>
            <SectionHeader
                label={
                    <div className="hidden text-lg md:block">
                        <div className="leading-[1.45rem] text-giphyWhite">{`Related ${SearchTypeCopy[type]}`}</div>
                        <div className="font-normal text-giphyLightCharcoal">{gif.title}</div>
                    </div>
                }
                className="mb-2.5 items-end"
            >
                <Toggle
                    onClick={() => {}}
                    className="mt-2 w-full md:mt-0 md:w-[400px]"
                    onTransitionEnd={(index: number) => {
                        const nextType = contentTypes[index]
                        setType(nextType)
                        setTransitioning(false)
                    }}
                    options={[{ label: 'GIFs' }, { label: 'Stickers' }, { label: 'Clips' }]}
                    index={contentTypes.indexOf(type)}
                />
            </SectionHeader>
            <div className="min-h-[1500px]">
                {isTransitioning ? (
                    <Loader className="animate-fade-in" />
                ) : type === 'clips' ? (
                    <VideoGrid
                        gaTrackingUrl={(page) => `/related/${id}/clips/page/${page}`}
                        gridWidth={width}
                        key={type}
                        columns={columns}
                        fetchGifs={doFetch}
                    />
                ) : (
                    <>
                        {videoData?.gif && (
                            <SpotlightContainer>
                                <VideoSpotlight data={videoData} className="mb-1.5 md:mb-0" />
                            </SpotlightContainer>
                        )}
                        {clickableAd?.gif && clientRender && type === 'gifs' && (
                            <ClickableAd
                                ad={clickableAd}
                                gif={clickableAd.gif}
                                width={isMobile ? windowWidth / 2 - 3 : clickableAdWidth}
                                isMobile={isMobile}
                            />
                        )}
                        <FreestarPlacement opportunity={freestarOpportunity} />
                        <Grid
                            initialGifs={type === 'gifs' ? relatedGifs : []}
                            width={width}
                            key={type}
                            columnOffsets={columnOffsets}
                            columns={columns}
                            fetchGifs={doFetch}
                            onGifClick={(gif, event) => {
                                const contentType = gif.is_sticker ? 'sticker' : 'gif'
                                const data = {
                                    event: 'related_content_click',
                                    options: {
                                        eventInfo: {
                                            navElement: 'related_content',
                                            navSubelement: 'related_' + contentType,
                                            link: gif.url,
                                        },
                                        content: {
                                            contentId: gif.id,
                                            contentType,
                                            contentIndex: ids.current.indexOf(gif.id),
                                        },
                                    },
                                }
                                setGADataLayer(data)
                                relativeGifClick(gif, event)
                            }}
                        />
                    </>
                )}
            </div>
        </section>
    ) : null
}
export default RelatedGrid
